/**
 * CircuitCanvas — animated SVG circuit board rendering the Target Journey.
 *
 * Layout: fixed 5-column × 3-row grid of CircuitNodes; SVG paths connect them
 * via known anchor points. Edges carry animated dots (SVG <animateMotion>)
 * whose speed scales with rupee throughput. Blocked edges render a stop sigil
 * + rose-tinted impact pill. Each node cascades into view on mount, status
 * lamps pulse, and target node glows louder as projection nears 100%.
 *
 * Pure presentational — no data fetching here. Caller supplies a JourneyState
 * from useTargetJourney().
 */
import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Instagram,
  Megaphone,
  Percent,
  PlugZap,
  Radio,
  ShoppingBag,
  Sparkles,
  Star,
  TrendingUp,
  Users,
  Wallet,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatINRCompact } from '@/lib/format';
import type { JourneyState, JourneyEdge, NodeId, Status } from './types';

// ───────── Layout grid (SVG logical coords; deterministic) ─────────

const NODE_W = 168;
const NODE_H = 76;

// 5-stage funnel laid out left → right in row 2.
// Inputs feed into Reach on row 3. Availability gate + AOV + Discount drag
// hang below the main funnel as side-modifiers. Target sits top-right.
const LAYOUT: Record<NodeId, { x: number; y: number; mountOrder: number }> = {
  // Inputs (bottom rail)
  meta_ads: { x: 140, y: 660, mountOrder: 0 },
  organic: { x: 330, y: 660, mountOrder: 0 },
  aggr_ads: { x: 520, y: 660, mountOrder: 0 },
  influence: { x: 710, y: 660, mountOrder: 0 },
  earned: { x: 900, y: 660, mountOrder: 0 },

  // Main funnel row (left → right)
  reach: { x: 180, y: 350, mountOrder: 1 },
  visitors: { x: 410, y: 350, mountOrder: 2 },
  carts: { x: 640, y: 350, mountOrder: 3 },
  orders: { x: 870, y: 350, mountOrder: 4 },

  // Channel split — horizontal triplet above Orders, all feed Target.
  channel_online: { x: 660, y: 215, mountOrder: 5 },
  channel_dinein: { x: 870, y: 215, mountOrder: 5 },
  channel_b2b: { x: 1080, y: 215, mountOrder: 5 },

  // Side modifiers — right column feeds into AOV.
  availability: { x: 410, y: 470, mountOrder: 2 },
  aov: { x: 1080, y: 350, mountOrder: 4 },
  discount: { x: 1080, y: 470, mountOrder: 4 },
  addons: { x: 1080, y: 560, mountOrder: 4 },

  target: { x: 1080, y: 80, mountOrder: 6 },
};

const ICONS: Record<string, LucideIcon> = {
  megaphone: Megaphone,
  instagram: Instagram,
  'trending-up': TrendingUp,
  sparkles: Sparkles,
  star: Star,
  radio: Radio,
  users: Users,
  'plug-zap': PlugZap,
  'shopping-bag': ShoppingBag,
  wallet: Wallet,
  percent: Percent,
  zap: Zap,
};

const STATUS_COLOR: Record<Status, { ring: string; lampHex: string; pill: string; aura: string }> = {
  good: {
    ring: 'ring-emerald-500/40',
    lampHex: '#10b981',
    pill: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    aura: 'shadow-[0_0_24px_-6px_rgba(16,185,129,0.45)]',
  },
  warn: {
    ring: 'ring-amber-500/40',
    lampHex: '#f59e0b',
    pill: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
    aura: 'shadow-[0_0_22px_-8px_rgba(245,158,11,0.45)]',
  },
  bad: {
    ring: 'ring-rose-500/60',
    lampHex: '#f43f5e',
    pill: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
    aura: 'shadow-[0_0_26px_-6px_rgba(244,63,94,0.55)]',
  },
  na: {
    ring: 'ring-border/40',
    lampHex: '#9ca3af',
    pill: 'bg-muted/60 text-muted-foreground',
    aura: '',
  },
};

// ───────── Component ─────────

export function CircuitCanvas({ state }: { state: JourneyState }) {
  const reduceMotion = useReducedMotion();
  const [hovered, setHovered] = useState<NodeId | null>(null);

  return (
    <div className="relative w-full rounded-xl border border-border/40 bg-gradient-to-br from-card/60 via-card/40 to-violet-500/5 p-2 md:p-4">
      <svg
        viewBox="0 0 1200 720"
        preserveAspectRatio="xMidYMid meet"
        className="block h-auto w-full"
        role="img"
        aria-label="Target Journey circuit diagram"
      >
        <defs>
          <filter id="lamp-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="bulb-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Bulb halo behind target node — pulses with target proximity */}
        <BulbHalo target={state.target} reduceMotion={!!reduceMotion} />

        {/* Edges first (under nodes) */}
        {state.edges.map((edge, i) => (
          <CircuitEdge
            key={`${edge.from}-${edge.to}-${i}`}
            edge={edge}
            reduceMotion={!!reduceMotion}
            highlighted={hovered === edge.from || hovered === edge.to}
            delay={LAYOUT[edge.from].mountOrder * 0.12 + 0.05}
          />
        ))}

        {/* Nodes */}
        {Object.values(state.nodes).map((node) => (
          <CircuitNode
            key={node.id}
            node={node}
            reduceMotion={!!reduceMotion}
            onHoverChange={(over) => setHovered(over ? node.id : null)}
            targetProgress={
              node.id === 'target' && state.target.target > 0
                ? Math.min(state.target.projected / state.target.target, 1)
                : undefined
            }
          />
        ))}

        {/* Voltage gauge moved INTO the target card (CircuitNode renders it). */}
      </svg>

      {/* Floating hover-detail panel — one place for all the per-node detail
          (sub-text + click hint), so node cards stay clean. Pinned to the
          top-right corner of the canvas; cards aren't crowded by inline copy. */}
      {hovered && state.nodes[hovered] && (state.nodes[hovered].sub || state.nodes[hovered].href) && (
        <div
          className={cn(
            'pointer-events-none absolute left-4 top-4 z-20 max-w-[280px] rounded-md border border-border/70 bg-card/95 p-3 text-xs shadow-lg backdrop-blur',
            'ring-1',
            STATUS_COLOR[state.nodes[hovered].status].ring,
          )}
        >
          <div className="flex items-center gap-1.5 text-2xs uppercase tracking-wide text-muted-foreground">
            <Lamp status={state.nodes[hovered].status} reduceMotion={!!reduceMotion} />
            <span>{state.nodes[hovered].label}</span>
          </div>
          <div className="mt-1 font-semibold tabular-nums">{state.nodes[hovered].value}</div>
          {state.nodes[hovered].sub && (
            <div className="mt-1.5 text-muted-foreground">{state.nodes[hovered].sub}</div>
          )}
          {state.nodes[hovered].href && (
            <div className="mt-2 text-2xs text-violet-500">
              click → {state.nodes[hovered].href}
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="mt-2 flex flex-wrap items-center gap-3 px-1 text-2xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /> on / ahead
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-amber-500" /> within 10%
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-rose-500" /> &gt; 10% short
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-muted-foreground/40" /> no data yet
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-px w-4 border-t border-dashed border-violet-400/60" /> discovery (halo)
        </span>
        <span className="ml-auto">Hover any node for detail · click to drill in</span>
      </div>
    </div>
  );
}

// ───────── CircuitNode ─────────

function CircuitNode({
  node,
  reduceMotion,
  onHoverChange,
  targetProgress,
}: {
  node: JourneyState['nodes'][NodeId];
  reduceMotion: boolean;
  onHoverChange: (over: boolean) => void;
  targetProgress?: number;
}) {
  const navigate = useNavigate();
  const isTarget = node.id === 'target';
  const w = NODE_W + (isTarget ? 36 : 0);
  const h = NODE_H + (isTarget ? 56 : 0); // taller for target → fits progress bar
  // Centre the foreignObject on the layout point. Target uses its expanded
  // h so the bottom of the card doesn't drift down into the channels row.
  const x = LAYOUT[node.id].x - w / 2;
  const y = LAYOUT[node.id].y - h / 2;
  const delay = LAYOUT[node.id].mountOrder * 0.12;

  return (
    <foreignObject x={x} y={y} width={w} height={h} style={{ overflow: 'visible' }}>
      <motion.div
        className={cn(
          'group flex h-full w-full flex-col justify-center rounded-lg border border-border/60 bg-card px-3 py-2 ring-1 transition-all',
          STATUS_COLOR[node.status].ring,
          // N/A nodes get a dashed border so they read as "needs wiring"
          // rather than "broken / dead card".
          node.status === 'na' && !isTarget && 'border-dashed bg-muted/20',
          isTarget && 'bg-gradient-to-br from-violet-500/15 via-card to-card',
          STATUS_COLOR[node.status].aura,
          node.href && 'cursor-pointer hover:scale-[1.03] hover:shadow-lg',
        )}
        initial={reduceMotion ? false : { opacity: 0, y: 10 }}
        animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay, ease: 'easeOut' }}
        onClick={() => node.href && navigate(node.href)}
        onMouseEnter={() => onHoverChange(true)}
        onMouseLeave={() => onHoverChange(false)}
        role={node.href ? 'button' : undefined}
        tabIndex={node.href ? 0 : undefined}
        title={node.href ? `Open ${node.href}` : undefined}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-2xs uppercase tracking-wide text-muted-foreground">
            <Lamp status={node.status} reduceMotion={reduceMotion} />
            {(() => {
              const Icon = node.icon ? ICONS[node.icon] : null;
              return Icon ? <Icon className="h-3 w-3" /> : null;
            })()}
            <span className="truncate">{node.label}</span>
          </div>
        </div>
        <div
          className={cn(
            'mt-0.5 truncate font-semibold tabular-nums leading-tight',
            isTarget ? 'text-xl' : 'text-base',
          )}
        >
          {node.value}
        </div>
        {isTarget && targetProgress !== undefined && (
          <div className="mt-2" title={`Projected month-end vs target: ${Math.round(targetProgress * 100)}%`}>
            <div className="flex items-center justify-between text-2xs">
              <span className="text-muted-foreground">Projected → target</span>
              <span className="font-semibold tabular-nums">
                {Math.round(targetProgress * 100)}%
              </span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted/50">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  targetProgress >= 1
                    ? 'bg-emerald-500'
                    : targetProgress >= 0.9
                      ? 'bg-amber-500'
                      : 'bg-rose-500',
                )}
                style={{ width: `${Math.min(targetProgress, 1) * 100}%` }}
              />
            </div>
          </div>
        )}
      </motion.div>
    </foreignObject>
  );
}

// ───────── Lamp (pulsing status indicator) ─────────

function Lamp({ status, reduceMotion }: { status: Status; reduceMotion: boolean }) {
  const color = STATUS_COLOR[status].lampHex;
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" className="shrink-0">
      <motion.circle
        cx="5"
        cy="5"
        r="3.5"
        fill={color}
        filter="url(#lamp-glow)"
        animate={
          reduceMotion || status === 'na'
            ? undefined
            : { opacity: [1, 0.55, 1], r: [3.5, 4.2, 3.5] }
        }
        transition={{ repeat: Infinity, duration: status === 'bad' ? 0.9 : 2, ease: 'easeInOut' }}
      />
    </svg>
  );
}

// ───────── CircuitEdge ─────────

function CircuitEdge({
  edge,
  reduceMotion,
  highlighted,
  delay,
}: {
  edge: JourneyEdge;
  reduceMotion: boolean;
  highlighted: boolean;
  delay: number;
}) {
  const from = LAYOUT[edge.from];
  const to = LAYOUT[edge.to];
  const blocked = !!edge.blocked;

  const path = useMemo(() => {
    const dx = to.x - from.x;
    const cp1x = from.x + dx * 0.5;
    const cp1y = from.y;
    const cp2x = from.x + dx * 0.5;
    const cp2y = to.y;
    return `M ${from.x},${from.y} C ${cp1x},${cp1y} ${cp2x},${cp2y} ${to.x},${to.y}`;
  }, [from.x, from.y, to.x, to.y]);

  const strokeWidth = useMemo(() => {
    const inr = edge.contributionInr ?? 0;
    return Math.min(6, Math.max(1.5, Math.log10(Math.max(inr, 1)) * 0.75));
  }, [edge.contributionInr]);

  const duration = useMemo(() => {
    const inr = edge.contributionInr ?? 0;
    if (inr <= 0) return 6;
    return Math.max(1.5, 6 - Math.log10(inr));
  }, [edge.contributionInr]);

  const mid = useMemo(
    () => ({ x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }),
    [from.x, from.y, to.x, to.y],
  );

  const pctLabel =
    edge.pctShare != null && Number.isFinite(edge.pctShare) && edge.pctShare > 0
      ? `${edge.pctShare.toFixed(0)}%`
      : null;

  const tooltip = edge.tooltip ?? (pctLabel ? `${pctLabel} share of upstream flow.` : '');

  return (
    <g>
      {/* Title element gives a native browser tooltip on hover over any
          part of this group — works on the path, label rect, dots. */}
      {tooltip && <title>{tooltip}</title>}
      <motion.path
        id={`edge-${edge.from}-${edge.to}`}
        d={path}
        fill="none"
        className={cn(
          blocked
            ? 'stroke-rose-500/70'
            : edge.kind === 'discovery'
              ? 'stroke-violet-400/35'
              : highlighted
                ? 'stroke-sky-400'
                : 'stroke-foreground/25',
        )}
        strokeWidth={
          edge.kind === 'discovery'
            ? 1 + (highlighted ? 0.8 : 0)
            : strokeWidth + (highlighted ? 1.5 : 0)
        }
        strokeLinecap="round"
        strokeDasharray={
          blocked ? '6 6' : edge.kind === 'discovery' ? '3 5' : undefined
        }
        initial={reduceMotion ? {} : { pathLength: 0, opacity: 0 }}
        animate={reduceMotion ? {} : { pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.7, delay, ease: 'easeInOut' }}
      />

      {!blocked && edge.kind !== 'discovery' && !reduceMotion && (
        <CurrentDots
          pathId={`edge-${edge.from}-${edge.to}`}
          duration={duration}
          color={highlighted ? '#0ea5e9' : '#38bdf8'}
        />
      )}

      {/* % label — slightly larger hit target so the tooltip is easy to catch */}
      {pctLabel && !blocked && edge.kind !== 'discovery' && (
        <g transform={`translate(${mid.x}, ${mid.y})`} className="cursor-help">
          <rect x={-26} y={-10} width={52} height={20} rx={10} className="fill-card stroke-border/60" />
          <text x={0} y={4} textAnchor="middle" className="fill-foreground text-[10px] font-semibold">
            {pctLabel}
          </text>
        </g>
      )}

      {blocked && edge.blocked && (
        <g transform={`translate(${mid.x}, ${mid.y})`}>
          <motion.rect
            x={-95}
            y={-15}
            width={190}
            height={30}
            rx={6}
            className="fill-rose-500/15 stroke-rose-500/50"
            initial={reduceMotion ? {} : { scale: 0 }}
            animate={reduceMotion ? {} : { scale: 1 }}
            transition={{ delay: delay + 0.4, duration: 0.3 }}
          />
          <text x={0} y={-2} textAnchor="middle" className="fill-rose-700 dark:fill-rose-300 text-[10px] font-bold">
            ⊥ BLOCKED · -{formatINRCompact(edge.blocked.costInr)}
          </text>
          <text x={0} y={10} textAnchor="middle" className="fill-rose-700 dark:fill-rose-300 text-[9px]">
            {edge.blocked.reason.slice(0, 44)}
          </text>
        </g>
      )}
    </g>
  );
}

function CurrentDots({
  pathId,
  duration,
  color,
}: {
  pathId: string;
  duration: number;
  color: string;
}) {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <circle key={i} r="2.8" fill={color}>
          <animateMotion dur={`${duration}s`} repeatCount="indefinite" begin={`${(duration / 3) * i}s`}>
            <mpath href={`#${pathId}`} />
          </animateMotion>
          <animate
            attributeName="opacity"
            values="0.4;1;1;0.4"
            keyTimes="0;0.15;0.85;1"
            dur={`${duration}s`}
            begin={`${(duration / 3) * i}s`}
            repeatCount="indefinite"
          />
        </circle>
      ))}
    </>
  );
}

// ───────── BulbHalo (projection-proximity glow behind target node) ─────────

function BulbHalo({ target, reduceMotion }: { target: JourneyState['target']; reduceMotion: boolean }) {
  const pct = target.target > 0 ? Math.min(target.projected / target.target, 1.1) : 0;
  const x = LAYOUT.target.x;
  const y = LAYOUT.target.y;
  return (
    <motion.circle
      cx={x}
      cy={y}
      r={120}
      fill="url(#bulb-glow)"
      animate={
        reduceMotion
          ? undefined
          : { opacity: [0.5 * pct + 0.1, 0.9 * pct + 0.2, 0.5 * pct + 0.1], r: [115, 130, 115] }
      }
      transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
    />
  );
}
